/* eslint-disable no-unused-vars */
type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type BuiltInRule = 'required' | 'email' | 'plz' | 'number' | 'checkbox' | 'password';

type ValidatorFn = (field: FormField) => boolean;

export interface ValidatorOptions {
	fieldSelector?: string;
	validationAttr?: string;
	getErrorElement?: (field: FormField) => HTMLElement | null;
	messages?: Partial<Record<BuiltInRule | string, string>>;
	onFieldValidated?: (field: FormField, valid: boolean) => void;
}

const DEFAULT_MESSAGES_EN: Record<BuiltInRule, string> = {
	required: 'This field is required.',
	email: 'Please enter a valid email address.',
	plz: 'Please enter a valid postal code.',
	number: 'Please enter a valid number.',
	checkbox: 'Please accept the privacy policy.',
	password:
		'The password must contain at least 8 characters including an uppercase letter, lowercase letter and number.',
};

const DEFAULT_MESSAGES_DE: Record<BuiltInRule, string> = {
	required: 'Das Feld darf nicht leer sein.',
	email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
	plz: 'Bitte geben Sie eine gültige Postleitzahl ein.',
	number: 'Bitte geben Sie eine gültige Nummer ein.',
	checkbox: 'Bitte stimmen Sie unserer Datenschutzerklärung zu.',
	password:
		'Das Passwort muss mindestens 8 Zeichen, und je mindestens einen Groß- und Kleinbuchstaben sowie eine Zahl enthalten.',
};

const isEnglish = document.documentElement.lang === 'en';

let DEFAULT_MESSAGES: Record<BuiltInRule, string>;

if (isEnglish) {
	DEFAULT_MESSAGES = DEFAULT_MESSAGES_EN;
} else {
	DEFAULT_MESSAGES = DEFAULT_MESSAGES_DE;
}

function isCheckbox(field: FormField): field is HTMLInputElement {
	return (field as HTMLInputElement).type === 'checkbox';
}

function isFile(field: FormField): field is HTMLInputElement {
	return (field as HTMLInputElement).type === 'file';
}

export class Validator {
	private form: HTMLFormElement;
	private fields: FormField[];
	private options: Required<ValidatorOptions>;
	private validators: Record<string, ValidatorFn>;
	private errorCache = new WeakMap<FormField, HTMLElement | null>();

	constructor(form: HTMLFormElement, opts: ValidatorOptions = {}) {
		this.form = form;

		this.options = {
			fieldSelector: opts.fieldSelector ?? '.js-validate',
			validationAttr: opts.validationAttr ?? 'validation',
			getErrorElement:
				opts.getErrorElement ?? ((field: FormField) => document.getElementById(`${field.id}--error`) || null),
			messages: {
				...DEFAULT_MESSAGES,
				...(opts.messages ?? {}),
			},
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			onFieldValidated: typeof opts.onFieldValidated === 'function' ? opts.onFieldValidated : () => {},
		};

		this.fields = Array.from(form.querySelectorAll<FormField>(this.options.fieldSelector));

		this.validators = {
			required: this.validateRequired,
			email: this.validateEmail,
			plz: this.validatePlz,
			number: this.validateNumber,
			checkbox: this.validateCheckbox,
			password: this.validatePassword
		};

		this.addEventListeners();
	}

	private addEventListeners(): void {
		this.form.addEventListener('submit', (e) => this.handleSubmit(e));

		this.fields.forEach((field) => {
			const rules = this.getRules(field);
			if (!rules.length) {
				return;
			}

			if (isCheckbox(field) || field.tagName === 'SELECT') {
				field.addEventListener('change', () => this.validateField(field));
			} else {
				field.addEventListener('input', () => this.validateField(field));
				field.addEventListener('blur', () => this.validateField(field));
			}
		});
	}

	private getRules(field: FormField): string[] {
		const attr = `data-${this.options.validationAttr}`;
		const raw = field.getAttribute(attr);
		if (!raw) {
			return [];
		}

		return raw
			.split(/[|\s]+/)
			.map((s) => s.trim())
			.filter(Boolean);
	}

	private getErrorEl(field: FormField): HTMLElement | null {
		if (this.errorCache.has(field)) {
			return this.errorCache.get(field)!;
		}

		const group = field.closest('.form__item');
		let el: HTMLElement | null = null;

		if (group) {
			el = (group.querySelector(':scope > .form__error') as HTMLElement) || null;
			if (!el) {
				el = (group.querySelector('.form__error') as HTMLElement) || null;
			}
		}

		if (!el) {
			el = (field.parentElement?.querySelector('.form__error') as HTMLElement) || null;
		}

		if (!el) {
			el = this.options.getErrorElement(field);
		}

		if (!el) {
			el = document.createElement('span');
			el.className = 'form__error';
			if (group) {
				group.appendChild(el);
			} else {
				field.insertAdjacentElement('afterend', el);
			}
		}

		if (!el.id) {
			el.id = `${field.id || 'field'}--error`;
		}

		const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
		if (!describedBy.includes(el.id)) {
			describedBy.push(el.id);
			field.setAttribute('aria-describedby', describedBy.join(' '));
		}

		this.errorCache.set(field, el);
		return el;
	}

	private showError(field: FormField, rule: string): void {
		const errorEl = this.getErrorEl(field);
		if (errorEl) {
			const msg =
				this.options.messages[rule] ?? (DEFAULT_MESSAGES as Record<string, string>)[rule] ?? 'Ungültiger Wert.';
			errorEl.textContent = msg;
			errorEl.setAttribute('role', 'alert');
			errorEl.setAttribute('aria-live', 'polite');

			const id = errorEl.id;
			if (id) {
				const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
				if (!describedBy.includes(id)) {
					describedBy.push(id);
					field.setAttribute('aria-describedby', describedBy.join(' '));
				}
			}
		}
		field.classList.add('invalid');
		field.setAttribute('aria-invalid', 'true');
	}

	public validateField(field: FormField): boolean {
		const rules = this.getRules(field);
		if (!rules.length) {
			this.clearError(field);
			this.options.onFieldValidated(field, true);
			return true;
		}

		for (const rule of rules) {
			const validator = this.validators[rule];
			const ok = validator ? validator(field) : true;
			if (!ok) {
				this.showError(field, rule);
				this.options.onFieldValidated(field, false);
				return false;
			}
		}

		this.clearError(field);
		this.options.onFieldValidated(field, true);
		return true;
	}

	public validateForm(): boolean {
		let allValid = true;
		for (const field of this.fields) {
			if (!this.validateField(field)) {
				allValid = false;
			}
		}
		return allValid;
	}

	private handleSubmit(event: Event): void {
		const ok = this.validateForm();
		if (!ok) {
			event.preventDefault();
			const firstInvalid = this.fields.find((f) => f.classList.contains('invalid'));
			firstInvalid?.focus();
		}
	}

	private clearError(field: FormField): void {
		const errorEl = this.getErrorEl(field);
		if (errorEl) {
			errorEl.textContent = '';
			errorEl.removeAttribute('role');
		}
		field.classList.remove('invalid');
		field.removeAttribute('aria-invalid');
	}

	private validateRequired: ValidatorFn = (field) => {
		if (isCheckbox(field)) {
			return field.checked;
		}
		if (isFile(field)) {
			return field.files !== null && field.files.length > 0;
		}
		return (field.value ?? '').trim().length > 0;
	};

	private validateEmail: ValidatorFn = (field) => {
		const v = (field.value ?? '').trim();
		if (v === '') {
			return true;
		}
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
	};

	private validatePassword: ValidatorFn = (field) => {
		const v = field.value;
		if (v.length < 8 || v.length > 64) {
			return false;
		}

		// Check for at least one uppercase letter
		if (!/[A-Z]/.test(v)) {
			return false;
		}

		// Check for at least one lowercase letter
		if (!/[a-z]/.test(v)) {
			return false;
		}

		// Check for at least one number
		if (!/[0-9]/.test(v)) {
			return false;
		}

		// Passed all checks
		return true;
	};

	private validatePlz: ValidatorFn = (field) => {
		const v = (field.value ?? '').trim();
		if (v === '') {
			return true;
		}
		return /^[0-9]{4,5}$/.test(v);
	};

	private validateNumber: ValidatorFn = (field) => {
		const v = (field.value ?? '').trim();
		if (v === '') {
			return true;
		}
		return /^\d+$/.test(v);
	};

	private validateCheckbox: ValidatorFn = (field) => {
		return isCheckbox(field) ? field.checked : true;
	};

	public reset(): void {
		this.fields.forEach((f) => this.clearError(f));
	}
}
